import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Card, Row, Col, Table, Spinner } from "react-bootstrap";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell
} from "recharts";
import { format, Locale, parseISO } from "date-fns";
import { enUS, vi, ko, zhCN } from 'date-fns/locale';
import { okx } from "../api/okx"; // Import to get Pi price
import { PiscanAPI } from "../api/piscan";
import { CardContent } from "../components/Card";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

interface UnlockMonthlyItem {
  claim_month: string;
  total_amount: number;
}

interface MainnetSupplyData {
  circulating_supply: number;
  migrated_mining_rewards: number;
  total_circulating_supply: number;
  total_locked: number;
  total_supply: number;
  updated_at: string;
}

const UnlockStatisticsFull: React.FC = () => {
  const { t, language } = useLanguage();
  const [unlockData, setUnlockData] = useState<UnlockMonthlyItem[]>([]);
  const [mainnetSupply, setMainnetSupply] = useState<MainnetSupplyData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [generatedAt, setGeneratedAt] = useState<string>("");
  const [highestMonth, setHighestMonth] = useState<{date: string, amount: number}>({date: "", amount: 0});
  const [piPrice, setPiPrice] = useState<number>(2.0); // Default value before loading

  // Get date-fns locale based on selected language
  const getDateLocale = () => {
    const locales: Record<string, Locale> = {
      'en': enUS,
      'vi': vi,
      'ko': ko,
      'zh': zhCN
    };
    return locales[language] || enUS;
  };

  useEffect(() => {
    setHeading(t('unlock_stats.monthly_heading') || "Monthly Pi Unlock Statistics");
    setTitle(t('unlock_stats.monthly_title') || "Monthly Pi Unlock Forecast");
    setDescription(t('unlock_stats.monthly_description') || "View projected Pi cryptocurrency unlocks by month");
  }, [setHeading, setTitle, setDescription, t, language]);

  useEffect(() => {
    fetchData();
  }, []);

  // Get Pi price
  useEffect(() => {
    fetchPiPrice();
    // Refresh price every 30 seconds
    const interval = setInterval(fetchPiPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPiPrice = async () => {
    try {
      const marketData = await okx.getMarketData();
      setPiPrice(parseFloat(marketData.idxPx));
    } catch (err) {
      console.error("Failed to fetch Pi price:", err);
      // Keep using the default price if fetch fails
    }
  };

  useEffect(() => {
    if (unlockData.length > 0) {
      // Find the month with highest unlock amount
      const highest = unlockData.reduce((max, current) => 
        current.total_amount > max.total_amount ? current : max, unlockData[0]);
      setHighestMonth({
        date: format(parseISO(highest.claim_month), 'MMM yyyy', { locale: getDateLocale() }),
        amount: highest.total_amount
      });
    }
  }, [unlockData, language]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch both datasets in parallel
      const [unlockResponse, supplyResponse] = await Promise.all([
        fetch(PiscanAPI.UnlockStatisticsFull).catch(() => ({ ok: false })),
        fetch(PiscanAPI.MainnetSupply).catch(() => ({ ok: false }))
      ]);
      
      // Handle unlock data
      let processedUnlockData = [];
      let generatedAtTimestamp = new Date().toISOString();
      
      if (unlockResponse.ok && 'json' in unlockResponse) {
        try {
          const unlockData = await unlockResponse.json();
          processedUnlockData = unlockData.data.map((item: { claim_month: string; total_amount: number; }) => ({
            ...item,
            formattedDate: format(parseISO(item.claim_month), 'MMM yyyy', { locale: getDateLocale() }),
            displayAmount: formatNumber(item.total_amount, 2)
          }));
          generatedAtTimestamp = unlockData.generated_at;
        } catch (err) {
          console.error("Error processing unlock data:", err);
          // Keep default empty array
        }
      }
      
      // Handle supply data
      let supplyData = {
        circulating_supply: 7049498342.1165,
        migrated_mining_rewards: 7049498342.1165,
        total_circulating_supply: 7049498342.1165,
        total_locked: 5321994189.59379,
        total_supply: 10845382064.7946,
        updated_at: new Date().toISOString()
      };
      
      if (supplyResponse.ok && 'json' in supplyResponse) {
        try {
          supplyData = await supplyResponse.json();
        } catch (err) {
          console.error("Error processing supply data:", err);
          // Keep default data
        }
      }
      
      setUnlockData(processedUnlockData);
      setMainnetSupply(supplyData);
      setGeneratedAt(generatedAtTimestamp);
    } catch (err) {
      console.error(`Failed to fetch data: ${err instanceof Error ? err.message : String(err)}`);
      setError(t('common.loading_error'));
      // Set default values if everything fails
      setUnlockData([]);
      setMainnetSupply({
        circulating_supply: 7049498342.1165,
        migrated_mining_rewards: 7049498342.1165,
        total_circulating_supply: 7049498342.1165,
        total_locked: 5321994189.59379,
        total_supply: 10845382064.7946,
        updated_at: new Date().toISOString()
      });
      setGeneratedAt(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  // Calculate total and average unlock amount
  const totalUnlockAmount = unlockData.reduce((sum, item) => sum + item.total_amount, 0);
  const averageMonthly = unlockData.length > 0 ? totalUnlockAmount / unlockData.length : 0;
  
  // Calculate percentage of total locked that will be unlocked
  const unlockPercentage = mainnetSupply && mainnetSupply.total_locked > 0 
    ? (totalUnlockAmount / (mainnetSupply.total_locked * 10_000_000)) * 100
    : 0;
  
  // Format number according to user locale
  const formatNumber = (value: number, decimalPlaces: number = 2): string => {
    return value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces
    });
  };

  // Format for display
  const formatPi = (value: number) => {
    return formatNumber(value, 2);
  };

  // Format for mainnet supply (already in Pi units)
  const formatMainnetPi = (value: number) => {
    return formatNumber(value, 2);
  };

  // Format USD value
  const formatUSD = (piAmount: number) => {
    const usdValue = (piAmount) * piPrice;
    return formatNumber(usdValue, 2);
  };

  // Format mainnet USD value
  const formatMainnetUSD = (piAmount: number) => {
    const usdValue = piAmount * piPrice;
    return formatNumber(usdValue, 2);
  };

  // Format Pi value with abbreviations (K, M, B)
  const formatPiAbbreviated = (value: number) => {
    const piValue = value;
    
    if (piValue >= 1000000) {
      return `${formatNumber(piValue / 1000000, 1)}${t('number.million_short')}`;
    } else if (piValue >= 1000) {
      return `${formatNumber(piValue / 1000, 1)}${t('number.thousand_short')}`;
    } else {
      return formatNumber(piValue, 2);
    }
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const piValue = formatPi(payload[0].value);
      const piAbbreviated = formatPiAbbreviated(payload[0].value);
      const usdValue = formatUSD(payload[0].value);
      
      return (
        <div className="custom-tooltip bg-white p-2 shadow-sm border">
          <p className="mb-1 fw-bold">{label}</p>
          <p className="mb-0">
            <span className="text-primary">{piAbbreviated} π</span>
            <span className="ms-1 small text-muted">({piValue})</span>
          </p>
          <p className="mb-0 text-muted">
            ~${usdValue}
          </p>
        </div>
      );
    }
    return null;
  };

  // Generate gradient colors for bars
  const getBarColor = (value: number) => {
    const max = highestMonth.amount;
    const intensity = value / max;
    // Generate a color from blue to purple based on intensity
    return `rgba(81, 45, 168, ${0.5 + intensity * 0.5})`;
  };

  // Calculate total for next 12 months
  const calculate12MonthTotal = (): number => {
    const now = new Date();
    const next12Months = unlockData
      .filter(item => {
        const itemDate = parseISO(item.claim_month);
        const twelveMonthsLater = new Date(now);
        twelveMonthsLater.setMonth(now.getMonth() + 12);
        return itemDate >= now && itemDate <= twelveMonthsLater;
      })
      .reduce((sum, item) => sum + item.total_amount, 0);
    
    return next12Months;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="unlock-statistics-page">
      <div className="mb-4">
        <p className="text-muted">
          {t('unlock_stats.monthly_intro_text') || "View projected Pi cryptocurrency unlocks forecasted by month."}
          <small className="d-block mt-1">
            <i className="bi bi-info-circle me-1"></i>
            {t('unlock_stats.monthly_note') || "Projections are based on eligible unlock claims."}
          </small>
          <small className="d-block mt-1">
            {t('unlock_stats.updated_at')} {generatedAt ? format(parseISO(generatedAt), 'MMM dd, yyyy HH:mm', { locale: getDateLocale() }) : t('unlock_stats.unknown')}
          </small>
        </p>
      </div>
      <div className="row g-3 mb-4 d-block d-md-none">
        {/* <AdSenseUnit/> */}
        <AdUnit
                  className="67ccaa1c9302154e82f057e7"
                  width="300px"
                  height="100px"
                  centered={true}
                />
      </div>
      <Row className="mb-4">
        {/* Total Statistics (renamed from Total Forecast) */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.total_forecast') || "Total Statistics"}</h6>
              <h3 className="fw-bold text-primary" style={{fontSize: "1.5rem"}}>
                {formatPi(totalUnlockAmount)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(totalUnlockAmount)}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* NEW CARD: Total lock next 12 months */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{"Total next 12 months"}</h6>
              <h3 className="fw-bold text-danger" style={{fontSize: "1.5rem"}}>
                {formatPi(calculate12MonthTotal())} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(calculate12MonthTotal())}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Average Monthly Unlock */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.avg_monthly') || "Avg Monthly"}</h6>
              <h3 className="fw-bold text-info" style={{fontSize: "1.5rem"}}>
                {formatPi(averageMonthly)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(averageMonthly)} {t('unlock_stats.per_month') || "per month"}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Highest Unlock Month */}
        <Col lg={3} md={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">{t('unlock_stats.highest_month') || "Highest Month"}</h6>
              <h3 className="fw-bold text-warning" style={{fontSize: "1.5rem"}}>
                {formatPi(highestMonth.amount)} π
              </h3>
              <p className="text-muted small">
                ~${formatUSD(highestMonth.amount)} {t('unlock_stats.in') || "in"} {highestMonth.date}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
          <h2 className="h5 mb-0 text-color-normal">{t('unlock_stats.monthly_chart_title') || "Pi Unlock Forecast by Month"}</h2>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={unlockData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 70
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="formattedDate" 
                angle={-45} 
                textAnchor="end"
                height={70}
                tick={{fontSize: 12}}
              />
              <YAxis 
                tickFormatter={(value) => formatPiAbbreviated(value)}
                label={{ 
                  value: t('unlock_stats.y_axis_label'), 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="total_amount" 
                name={t('unlock_stats.pi_unlock')}
                radius={[4, 4, 0, 0]}
              >
                {unlockData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.total_amount)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
      
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
          <h2 className="h5 mb-0 text-color-normal">{t('unlock_stats.monthly_data_table') || "Monthly Unlock Data"}</h2>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('unlock_stats.month')}</th>
                  <th className="text-end">{t('unlock_stats.amount')} (π)</th>
                  <th className="text-end">{t('unlock_stats.value')} (USD)</th>
                </tr>
              </thead>
              <tbody>
                {unlockData.map((item, index) => (
                  <tr key={index}>
                    <td>{format(parseISO(item.claim_month), 'MMMM yyyy', { locale: getDateLocale() })}</td>
                    <td className="text-end fw-bold">
                      {formatPi(item.total_amount)} π
                    </td>
                    <td className="text-end text-muted">
                      ${formatUSD(item.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      
      {/* Add link back to daily stats */}
      <div className="text-center mt-4">
        <a href="/unlock-statistics" className="btn btn-outline-primary">
          {t('unlock_stats.view_daily') || "View Daily Unlock Statistics"}
        </a>
      </div>
    </div>
  );
};

export default UnlockStatisticsFull;